import { GenericBackendResponse } from '../genericBackendResponse';
import { ARCA_PLANS, ArcaPlan } from '../../constants/arcaPlans';
import getCatalogPlanBySlug, {
	PensionPlanCatalogItem
} from '../pension-plan-transfers/getCatalogPlanBySlug';

export type PlanType = 'EMPLEO' | 'INDIVIDUAL';

export interface PensionPlanRegistryItem {
	planCodigoN: string;
	planName: string;
	planType?: PlanType;
	return1Y: number | null;
	return3Y: number | null;
	return5Y: number | null;
	return10Y: number | null;
	commissionManagement: number | null;
	commissionDepositary: number | null;
}

interface PensionPlanRegistryList {
	items: PensionPlanRegistryItem[];
	totalItems: number;
}

/** Cabeceras de lectura con la sesión del usuario. Nunca `x-password`: iría en el bundle público. */
const cabecerasConSesion = (token: string) => ({
	'Content-Type': 'application/json',
	Authorization: `Bearer ${token}`
});

// Returns (rentabilidad) — del registro, con la sesión del usuario
async function fetchRegistryPlan(
	dgsfpCode: string,
	token: string
): Promise<PensionPlanRegistryItem | null> {
	if (!token) return null;

	try {
		const response = await fetch(
			`${process.env.REACT_APP_BASE_API}/pension-plan-registry/search?q=${dgsfpCode}&page=1&limit=1`,
			{
				method: 'GET',
				headers: cabecerasConSesion(token)
			}
		);
		if (!response.ok) return null;
		const json: GenericBackendResponse<PensionPlanRegistryList> = await response.json();
		return json.data?.items?.[0] ?? null;
	} catch {
		return null;
	}
}

// Commissions — del catálogo por slug (una petición por plan), con la sesión del usuario
async function fetchCatalogPlan(
	slug: string,
	token: string
): Promise<PensionPlanCatalogItem | null> {
	if (!token) return null;
	try {
		const res = await getCatalogPlanBySlug(slug, token);
		if (!res.success || !res.data) return null;
		return res.data;
	} catch {
		return null;
	}
}

// Combines catalog (commissions) + registry (name) for our commercialised plans. The return of
// each plan comes from comparsa (`/plan-returns`, `useArcaPlanReturns`), never from here.
// Falls back to the ARCA_PLANS values for any field the APIs don't return.
export async function getArcaPlansFinancials(token: string): Promise<ArcaPlan[]> {
	const [catalogResults, registryResults] = await Promise.all([
		Promise.all(ARCA_PLANS.map((plan) => fetchCatalogPlan(plan.catalogSlug, token))),
		// Del registro solo se usa el nombre, y solo en los planes que no son de autónomos.
		Promise.all(
			ARCA_PLANS.map((plan) =>
				plan.isFreelanceOnly ? Promise.resolve(null) : fetchRegistryPlan(plan.dgsfpCode, token)
			)
		)
	]);

	return ARCA_PLANS.map((plan, i) => {
		const catalog = catalogResults[i];
		const registry = registryResults[i];

		const commissionMgmt =
			catalog?.commissionManagement != null
				? parseFloat(catalog.commissionManagement)
				: plan.commissionMgmt;

		return {
			...plan,
			// Autónomos: usamos nuestro nombre curado (el del registry es confuso, p.ej.
			// N5489 "INDEXA EMPLEO ACCIONES AUTONOMOS" es en realidad el de ciclo de vida).
			// Individuales: mantenemos el nombre del registry como hasta ahora.
			name: plan.isFreelanceOnly ? plan.name : registry?.planName ?? plan.name,
			commissionMgmt
		};
	});
}
